import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/profile-components/Navbar';
import Topbar from '../components/profile-components/Topbar';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './SalesPages.css';
import { authFetch } from '../utils/authFetch';

const escapeCsvValue = (value) => {
  const stringValue = value === null || value === undefined ? '' : String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
};

const downloadCsv = (filename, rows) => {
  if (!rows.length) return;

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.map(escapeCsvValue).join(','),
    ...rows.map((row) => headers.map((header) => escapeCsvValue(row[header])).join(',')),
  ].join('\n');

  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

function Leads() {
  const [leads, setLeads] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [coverageTypeFilter, setCoverageTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortAsc, setSortAsc] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function loadLeads() {
      try {
        setLoading(true);

        const [leadsRes, statusesRes] = await Promise.all([
          authFetch('/api/leads'),
          authFetch('/api/lead-statuses'),
        ]);

        if (!leadsRes.ok || !statusesRes.ok) {
          throw new Error('Failed to load leads data');
        }

        const leadsData = await leadsRes.json();
        const statusesData = await statusesRes.json();

        const statusMap = {};
        (statusesData || []).forEach((s) => {
          statusMap[s.id] = s.label || s.name || '';
        });

        setLeads(Array.isArray(leadsData) ? leadsData : []);
        setStatuses(statusMap);
      } catch (error) {
        console.error('Error loading leads:', error);
        setLeads([]);
        setStatuses({});
      } finally {
        setLoading(false);
      }
    }

    loadLeads();
  }, []);

  const filteredLeads = useMemo(() => {
    return leads
      .filter((lead) => {
        const matchesStatus = statusFilter
          ? lead.lead_status_id === parseInt(statusFilter, 10)
          : true;

        const matchesCoverage = coverageTypeFilter
          ? lead.policies?.some((p) => p.coverageTypeName === coverageTypeFilter)
          : true;

        const matchesSearch = searchTerm
          ? `${lead.first_name} ${lead.last_name}`
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          : true;

        return matchesStatus && matchesCoverage && matchesSearch;
      })
      .sort((a, b) => {
        if (!sortField) return 0;

        const valA = a[sortField] || '';
        const valB = b[sortField] || '';

        if (valA < valB) return sortAsc ? -1 : 1;
        if (valA > valB) return sortAsc ? 1 : -1;
        return 0;
      });
  }, [leads, statusFilter, coverageTypeFilter, searchTerm, sortField, sortAsc]);

  const uniqueCoverageTypes = Array.from(
    new Set(
      leads.flatMap((l) => l.policies?.map((p) => p.coverageTypeName)).filter(Boolean)
    )
  );

  const handleSort = (field) => {
    if (sortField === field) setSortAsc(!sortAsc);
    else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const statusClass = (value) => {
    const normalized = String(value || '')
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/\//g, '_')
      .replace(/-+/g, '_');

    return `sales-chip ${normalized || 'sales-chip-neutral'}`;
  };

  const exportCSV = () => {
    downloadCsv(
      'leads.csv',
      filteredLeads.map((lead) => ({
        Name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim(),
        Status: statuses[lead.lead_status_id] || '',
        'Coverage Type': lead.policies?.map((p) => p.coverageTypeName).join(', ') || '',
        'Phone #': lead.phone || '',
        'Email Address': lead.email || '',
      }))
    );

    setExportOpen(false);
  };

  const exportPDF = () => {
    const doc = new jsPDF();

    autoTable(doc, {
      head: [['Lead Name', 'Status', 'Coverage Type', 'Phone #', 'Email Address']],
      body: filteredLeads.map((lead) => [
        `${lead.first_name || ''} ${lead.last_name || ''}`.trim(),
        statuses[lead.lead_status_id] || '-',
        lead.policies?.map((p) => p.coverageTypeName).join(', ') || '-',
        lead.phone || '-',
        lead.email || '-',
      ]),
      startY: 20,
      styles: { fontSize: 8 },
    });

    doc.save('leads.pdf');
    setExportOpen(false);
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">Leads</h1>
            <p className="sales-subtitle">
              View, filter, and manage all leads across coverage types in one place.
            </p>
          </div>

          <button
            className="sales-primary-btn"
            onClick={() => navigate('/new-lead')}
          >
            + New Lead
          </button>
        </div>

        <div className="sales-toolbar-card">
          <div
            style={{
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="sales-search-input"
              style={{ maxWidth: '220px' }}
            >
              <option value="">All Statuses</option>
              {Object.entries(statuses).map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>

            <select
              value={coverageTypeFilter}
              onChange={(e) => setCoverageTypeFilter(e.target.value)}
              className="sales-search-input"
              style={{ maxWidth: '240px' }}
            >
              <option value="">All Coverage Types</option>
              {uniqueCoverageTypes.map((ct) => (
                <option key={ct} value={ct}>
                  {ct}
                </option>
              ))}
            </select>

            <input
              type="text"
              className="sales-search-input"
              placeholder="Search leads by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ minWidth: '260px', flex: 1 }}
            />

            <div style={{ position: 'relative', marginLeft: 'auto' }}>
              <button
                onClick={() => setExportOpen(!exportOpen)}
                className="sales-secondary-btn"
                type="button"
              >
                Export Leads ▾
              </button>

              {exportOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    minWidth: '160px',
                    background: '#fff',
                    border: '1px solid rgba(226, 232, 240, 0.95)',
                    borderRadius: '14px',
                    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
                    overflow: 'hidden',
                    zIndex: 50,
                  }}
                >
                  <button
                    onClick={exportCSV}
                    type="button"
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 12px',
                      border: 'none',
                      background: '#fff',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    Export as CSV
                  </button>
                  <button
                    onClick={exportPDF}
                    type="button"
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 12px',
                      border: 'none',
                      background: '#fff',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    Export as PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sales-table-card">
          <div className="sales-table-header">
            <div>
              <h2>All Leads</h2>
              <p>
                {filteredLeads.length} lead{filteredLeads.length === 1 ? '' : 's'}
              </p>
            </div>
          </div>

          <div className="sales-table-wrap">
            <table className="sales-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('first_name')}>Lead</th>
                  <th onClick={() => handleSort('lead_status_id')}>Status</th>
                  <th>Coverage Type</th>
                  <th onClick={() => handleSort('phone')}>Phone</th>
                  <th onClick={() => handleSort('email')}>Email</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="sales-empty-cell">
                      Loading leads...
                    </td>
                  </tr>
                ) : filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="sales-empty-cell">
                      No leads found.
                    </td>
                  </tr>
                ) : (
                  filteredLeads.map((lead) => (
                    <tr
                      key={lead.id}
                      className="sales-click-row"
                      onClick={() => navigate(`/lead-profile/${lead.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="sales-primary-cell">
                          <div className="sales-name">
                            {lead.first_name} {lead.last_name}
                          </div>
                          <div className="sales-secondary">
                            Lead ID: {lead.id}
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className={statusClass(statuses[lead.lead_status_id] || 'Unknown')}>
                          {statuses[lead.lead_status_id] || 'Unknown'}
                        </span>
                      </td>

                      <td>
                        <div className="sales-primary-cell">
                          <div className="sales-name">
                            {lead.policies?.length
                              ? lead.policies.map((p) => p.coverageTypeName).join(', ')
                              : '—'}
                          </div>
                        </div>
                      </td>

                      <td onClick={(e) => e.stopPropagation()}>
                        {lead.phone ? (
                          <a href={`tel:${lead.phone}`} className="sales-secondary-btn" style={{ padding: '6px 10px', textDecoration: 'none' }}>
                            {lead.phone}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>

                      <td onClick={(e) => e.stopPropagation()}>
                        {lead.email ? (
                          <a href={`mailto:${lead.email}`} className="sales-secondary-btn" style={{ padding: '6px 10px', textDecoration: 'none' }}>
                            {lead.email}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default Leads;